import React from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import WidgetSkeleton from "../widget/WidgetSkeleton";
import { formatNumber } from "../../utils/formatter";

interface SeriesItem {
  name: string;
  data: { [key: string]: number };
}
interface MonthlyData {
  series: SeriesItem[];
}
interface Section4Props {
  pickupMonthly: MonthlyData | null;
  deliveryMonthly: MonthlyData | null;
  isLoadingPickupMonthly: boolean;
  isLoadingDeliveryMonthly: boolean;
}

const months = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Des",
];

function mapApiSeriesToApex(series: SeriesItem[]) {
  return series.map((item) => ({
    name: item.name,
    data: months.map((_, idx) => item.data[String(idx + 1)] ?? 0),
  }));
}

const Section4: React.FC<Section4Props> = ({
  pickupMonthly,
  deliveryMonthly,
  isLoadingPickupMonthly,
  isLoadingDeliveryMonthly,
}) => {
  const deliverySeries = deliveryMonthly
    ? mapApiSeriesToApex(deliveryMonthly.series)
    : [];
  const pickupSeries = pickupMonthly
    ? mapApiSeriesToApex(pickupMonthly.series)
    : [];

  const createChartOptions = (): ApexOptions => ({
    chart: {
      type: "bar" as const,
      height: 350,
      toolbar: { show: false },
    },
    colors: ["#4a2618", "#517b46", "#ffb4b4"],
    plotOptions: { bar: { horizontal: false, columnWidth: "90%" } },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    xaxis: {
      categories: months,
      axisBorder: { show: true },
      axisTicks: { show: true },
      labels: { style: { colors: "#000000" } },
    },
    yaxis: {
      min: 0,
      max: 100,
      tickAmount: 5,
      labels: {
        formatter: (val: number) => {
          return formatNumber(Math.round(val)) + "%";
        },
        style: { colors: "#000000" },
      },
    },
    grid: {
      borderColor: "#e0e0e0",
      strokeDashArray: 0,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
    },
    legend: {
      position: "top",
      horizontalAlign: "center",
      offsetY: 0,
      labels: { colors: "#000000" },
    },
    tooltip: {
      y: {
        formatter: (val) => val.toLocaleString(),
      },
    },
  });
  return (
    <div className="row mb-3">
      <div className="col-12 col-lg-6 mb-4 mb-lg-0">
        <div className="card bg-white p-4">
          {isLoadingPickupMonthly && <WidgetSkeleton />}
          <div className="text-gray-500 font-bold text-md mb-3">
            <span className="text-black fs-sm">
              MONTHLY DELIVERY SERVICE LEVEL
            </span>
          </div>
          <div
            className="flex justify-between mb-3"
            style={{ height: "350px", width: "100%" }}
          >
            <div style={{ width: "100%" }}>
              <ReactApexChart
                options={createChartOptions()}
                series={deliverySeries}
                type="bar"
                height={350}
                width="100%"
              />
            </div>
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-6">
        <div className="card bg-white p-4">
          {isLoadingDeliveryMonthly && <WidgetSkeleton />}
          <div className="text-gray-500 font-bold text-md mb-3">
            <span className="text-black fs-sm">
              MONTHLY PICKUP SERVICE LEVEL
            </span>
          </div>
          <div
            className="flex justify-between mb-3"
            style={{ height: "350px", width: "100%" }}
          >
            <div style={{ width: "100%" }}>
              <ReactApexChart
                options={createChartOptions()}
                series={pickupSeries}
                type="bar"
                height={350}
                width="100%"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Section4;
